import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { fetchProxyHosts, deleteProxyHost, testProxyHost, updateProxyHost, testProxyHostConfig, cloneProxyHost, toggleProxyHostFavorite } from '../api/proxy-hosts'
import { getCertificate } from '../api/certificates'
import type { ProxyHost } from '../types/proxy-host'
import type { ProxyHostTestResult } from '../types/proxy-host'
import { TestResultModal } from './proxy-host-list/TestResultModal'
import { CloneModal } from './proxy-host-list/CloneModal'
import { ProxyHostTable } from './proxy-host-list/ProxyHostTable'

interface ProxyHostListProps {
  onEdit: (host: ProxyHost, tab?: 'basic' | 'ssl' | 'security' | 'performance' | 'advanced' | 'protection') => void
  onAdd: () => void
}

interface HealthStatus {
  [hostId: string]: 'checking' | 'online' | 'offline' | 'unknown'
}


type SortBy = 'name' | 'updated' | 'created'
type SortOrder = 'asc' | 'desc'

const PER_PAGE_OPTIONS = [10, 20, 50] as const

export function ProxyHostList({ onEdit, onAdd }: ProxyHostListProps) {
  const { t } = useTranslation('proxyHost')
  const queryClient = useQueryClient()
  const [healthStatus, setHealthStatus] = useState<HealthStatus>({})
  const [testingHost, setTestingHost] = useState<ProxyHost | null>(null)
  const [testResult, setTestResult] = useState<ProxyHostTestResult | null>(null)
  const [testError, setTestError] = useState<string | null>(null)
  const [isTestLoading, setIsTestLoading] = useState(false)
  const [toggleConfirmHost, setToggleConfirmHost] = useState<ProxyHost | null>(null)
  const [cloningHost, setCloningHost] = useState<ProxyHost | null>(null)
  // Certificate issuance progress state for clone
  const [cloneCertCreating, setCloneCertCreating] = useState(false)
  const [cloneCertProgress, setCloneCertProgress] = useState<string | null>(null)
  const [cloneCertElapsedTime, setCloneCertElapsedTime] = useState(0)
  const [cloneCertError, setCloneCertError] = useState<string | null>(null)
  const [cloneCertSuccess, setCloneCertSuccess] = useState(false)
  const [searchInput, setSearchInput] = useState('')  // For controlled input
  const [searchQuery, setSearchQuery] = useState('')  // For actual query (debounced)
  const [currentPage, setCurrentPage] = useState(1)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput)
        setCurrentPage(1)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput, searchQuery])
  const [perPage, setPerPage] = useState<number>(() => {
    const saved = localStorage.getItem('proxyHostPerPage')
    return saved ? parseInt(saved, 10) : 20
  })
  const [sortBy, setSortBy] = useState<SortBy>(() => {
    const saved = localStorage.getItem('proxyHostSortBy')
    return (saved as SortBy) || 'name'
  })
  const [sortOrder, setSortOrder] = useState<SortOrder>(() => {
    const saved = localStorage.getItem('proxyHostSortOrder')
    return (saved as SortOrder) || 'asc'
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['proxy-hosts', currentPage, perPage, searchQuery, sortBy, sortOrder],
    queryFn: () => fetchProxyHosts(currentPage, perPage, searchQuery, sortBy, sortOrder),
  })

  const deleteMutation = useMutation({
    mutationFn: deleteProxyHost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proxy-hosts'] })
    },
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      updateProxyHost(id, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proxy-hosts'] })
    },
  })

  const favoriteMutation = useMutation({
    mutationFn: toggleProxyHostFavorite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proxy-hosts'] })
    },
  })

  const cloneMutation = useMutation({
    mutationFn: ({ id, domainNames, certificateId, certProvider, dnsProviderId, forwardScheme, forwardHost, forwardPort, isCreatingCert }: {
      id: string
      domainNames: string[]
      certificateId?: string
      certProvider?: string
      dnsProviderId?: string
      forwardScheme: string
      forwardHost: string
      forwardPort: number
      isCreatingCert: boolean
    }) =>
      cloneProxyHost(id, {
        domain_names: domainNames,
        certificate_id: certificateId,
        cert_provider: certProvider,
        dns_provider_id: dnsProviderId,
        forward_scheme: forwardScheme,
        forward_host: forwardHost,
        forward_port: forwardPort,
      }).then(result => ({ ...result, isCreatingCert })),
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['proxy-hosts'] })
      queryClient.invalidateQueries({ queryKey: ['certificates'] })

      // If creating new certificate, wait for it to complete
      if (data.isCreatingCert) {
        setCloneCertCreating(true)
        setCloneCertProgress(t('actions.cloneCertIssuing'))
        setCloneCertElapsedTime(0)
        setCloneCertError(null)

        const success = await waitForCloneCertificate(data.id)

        setCloneCertCreating(false)
        if (success) {
          setCloneCertSuccess(true)
          queryClient.invalidateQueries({ queryKey: ['proxy-hosts'] })
          queryClient.invalidateQueries({ queryKey: ['certificates'] })
          // Auto-close modal after 1.5 seconds on success
          setTimeout(() => resetCloneState(), 1500)
        }
        // On error, keep modal open to show error message
      } else {
        resetCloneState()
      }
    },
  })

  const handleToggle = (host: ProxyHost) => {
    setToggleConfirmHost(host)
  }

  const confirmToggle = () => {
    if (toggleConfirmHost) {
      toggleMutation.mutate({ id: toggleConfirmHost.id, enabled: !toggleConfirmHost.enabled })
      setToggleConfirmHost(null)
    }
  }

  // Check health status for all hosts on load
  useEffect(() => {
    const hosts = data?.data || []
    hosts.forEach((host) => {
      if (!healthStatus[host.id]) {
        checkHealth(host.id)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data])

  const checkHealth = async (hostId: string) => {
    setHealthStatus((prev) => ({ ...prev, [hostId]: 'checking' }))
    try {
      const result = await testProxyHost(hostId)
      setHealthStatus((prev) => ({
        ...prev,
        [hostId]: result.status === 'ok' ? 'online' : 'offline',
      }))
    } catch {
      setHealthStatus((prev) => ({ ...prev, [hostId]: 'offline' }))
    }
  }

  const handleDelete = async (id: string) => {
    if (confirm(t('actions.deleteConfirm'))) {
      deleteMutation.mutate(id)
    }
  }

  // Certificate polling function for clone modal
  const waitForCloneCertificate = async (hostId: string, maxWaitTime = 120000): Promise<boolean> => {
    const startTime = Date.now()
    const pollInterval = 2000

    while (Date.now() - startTime < maxWaitTime) {
      const elapsed = Math.floor((Date.now() - startTime) / 1000)
      setCloneCertElapsedTime(elapsed)

      try {
        // Get the host to find its certificate
        const hosts = await fetchProxyHosts(1, 100, '', 'name', 'asc')
        const host = hosts.data.find(h => h.id === hostId)

        if (!host?.certificate_id) {
          setCloneCertProgress(`${t('actions.cloneCertIssuing')} (${elapsed}s)`)
          await new Promise(resolve => setTimeout(resolve, pollInterval))
          continue
        }

        const cert = await getCertificate(host.certificate_id)

        if (!cert) {
          setCloneCertProgress(`${t('actions.cloneCertIssuing')} (${elapsed}s)`)
          await new Promise(resolve => setTimeout(resolve, pollInterval))
          continue
        }

        if (cert.status === 'issued') {
          return true
        } else if (cert.status === 'error') {
          setCloneCertError(cert.error_message || t('actions.cloneCertFailed'))
          return false
        }

        setCloneCertProgress(`${t('actions.cloneCertIssuing')} (${elapsed}s)`)
      } catch (err) {
        console.error('Error checking certificate:', err)
        setCloneCertProgress(`${t('actions.cloneCertIssuing')} (${elapsed}s)`)
      }

      await new Promise(resolve => setTimeout(resolve, pollInterval))
    }

    setCloneCertError('Certificate issuance timed out')
    return false
  }

  // Reset all clone-related state
  const resetCloneState = () => {
    setCloningHost(null)
    setCloneCertCreating(false)
    setCloneCertProgress(null)
    setCloneCertElapsedTime(0)
    setCloneCertError(null)
    setCloneCertSuccess(false)
  }

  const handleClone = (host: ProxyHost) => {
    setCloningHost(host)
  }

  const handleTestConfig = async (host: ProxyHost) => {
    setTestingHost(host)
    setTestResult(null)
    setTestError(null)
    setIsTestLoading(true)

    try {
      const result = await testProxyHostConfig(host.id)
      setTestResult(result)
    } catch (err) {
      setTestError((err as Error).message)
    } finally {
      setIsTestLoading(false)
    }
  }

  const handleRetest = async () => {
    if (!testingHost) return
    handleTestConfig(testingHost)
  }

  // Hosts are now sorted server-side
  const hosts = data?.data || []

  // Handle search input change (debounced via useEffect)
  const handleSearchChange = (value: string) => {
    setSearchInput(value)
  }

  // Clear search
  const handleClearSearch = () => {
    setSearchInput('')
    setSearchQuery('')
    setCurrentPage(1)
  }

  // Handle per page change
  const handlePerPageChange = (value: number) => {
    setPerPage(value)
    setCurrentPage(1)
    localStorage.setItem('proxyHostPerPage', value.toString())
  }

  // Pagination helpers
  const totalPages = data?.total_pages || 1
  const total = data?.total || 0

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400">
        Error loading proxy hosts: {(error as Error).message}
      </div>
    )
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('list.title')}</h2>

        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {/* Search */}
          <div className="relative flex-1 sm:flex-none">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('list.search')}
              className="w-full sm:w-48 pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {searchInput && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-400"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Sort */}
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [by, order] = e.target.value.split('-') as [SortBy, SortOrder]
              setSortBy(by)
              setSortOrder(order)
              setCurrentPage(1)
              localStorage.setItem('proxyHostSortBy', by)
              localStorage.setItem('proxyHostSortOrder', order)
            }}
            className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="name-asc">{t('list.sort.nameAsc')}</option>
            <option value="name-desc">{t('list.sort.nameDesc')}</option>
            <option value="updated-desc">{t('list.sort.updatedDesc')}</option>
            <option value="updated-asc">{t('list.sort.updatedAsc')}</option>
            <option value="created-desc">{t('list.sort.createdDesc')}</option>
            <option value="created-asc">{t('list.sort.createdAsc')}</option>
          </select>

          <button
            onClick={onAdd}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('list.addNew')}
          </button>
        </div>
      </div>

      {hosts.length === 0 ? (
        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-8 text-center border border-dashed border-slate-200 dark:border-slate-700">
          <div className="w-12 h-12 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {searchInput ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              )}
            </svg>
          </div>
          <h3 className="text-slate-600 dark:text-slate-300 font-medium mb-1">
            {searchInput ? t('list.noResults') : t('list.empty')}
          </h3>
          <p className="text-slate-400 text-sm">
            {searchInput ? t('list.noResultsDescription', { query: searchInput }) : t('list.emptyDescription')}
          </p>
          {searchInput && (
            <button
              onClick={handleClearSearch}
              className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('list.clearSearch')}
            </button>
          )}
        </div>
      ) : (
        <ProxyHostTable
          hosts={hosts}
          healthStatus={healthStatus}
          onEdit={onEdit}
          onDelete={handleDelete}
          onToggle={handleToggle}
          onClone={handleClone}
          onTestConfig={handleTestConfig}
          onCheckHealth={checkHealth}
          onFavorite={(id) => favoriteMutation.mutate(id)}
          togglePending={toggleMutation.isPending}
        />
      )
      }


      {/* Pagination */}
      {total > 0 && (
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              {t('list.showing', {
                from: (currentPage - 1) * perPage + 1,
                to: Math.min(currentPage * perPage, total),
                total
              })}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 dark:text-slate-400">{t('list.perPage')}:</span>
              <select
                value={perPage}
                onChange={(e) => handlePerPageChange(Number(e.target.value))}
                className="px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {PER_PAGE_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* First Page */}
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('list.pagination.first')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
            {/* Previous Page */}
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('list.pagination.previous')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Page Numbers */}
            <div className="flex items-center gap-1 px-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`min-w-[32px] h-8 px-2 text-sm font-medium rounded-lg transition-colors ${
                      currentPage === pageNum
                        ? 'bg-primary-600 text-white'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>

            {/* Next Page */}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('list.pagination.next')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            {/* Last Page */}
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('list.pagination.last')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Test Result Modal */}
      {
        testingHost && (
          <TestResultModal
            host={testingHost}
            result={testResult}
            isLoading={isTestLoading}
            error={testError}
            onClose={() => {
              setTestingHost(null)
              setTestResult(null)
              setTestError(null)
            }}
            onRetest={handleRetest}
          />
        )
      }

      {/* Toggle Confirmation Modal */}
      {
        toggleConfirmHost && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {toggleConfirmHost.enabled ? t('actions.disableConfirmTitle') : t('actions.enableConfirmTitle')}
                </h3>
              </div>
              <div className="px-6 py-4">
                <p className="text-slate-600 dark:text-slate-400">
                  {toggleConfirmHost.enabled
                    ? t('actions.disableConfirmMessage', { domain: toggleConfirmHost.domain_names[0] })
                    : t('actions.enableConfirmMessage', { domain: toggleConfirmHost.domain_names[0] })}
                </p>
              </div>
              <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 flex justify-end gap-3">
                <button
                  onClick={() => setToggleConfirmHost(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  {t('common:buttons.cancel')}
                </button>
                <button
                  onClick={confirmToggle}
                  disabled={toggleMutation.isPending}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${toggleConfirmHost.enabled
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                    } disabled:opacity-50`}
                >
                  {toggleMutation.isPending
                    ? t('common:status.processing')
                    : toggleConfirmHost.enabled
                      ? t('actions.disable')
                      : t('actions.enable')}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Clone Modal */}
      {
        cloningHost && (
          <CloneModal
            host={cloningHost}
            isPending={cloneMutation.isPending}
            isError={cloneMutation.isError}
            error={cloneMutation.error as Error | null}
            onClone={(params) => {
              cloneMutation.mutate({
                id: cloningHost.id,
                domainNames: params.domainNames,
                certificateId: params.certificateId,
                certProvider: params.certProvider,
                dnsProviderId: params.dnsProviderId,
                forwardScheme: params.forwardScheme,
                forwardHost: params.forwardHost,
                forwardPort: params.forwardPort,
                isCreatingCert: params.isCreatingCert,
              })
            }}
            onClose={() => resetCloneState()}
            certCreating={cloneCertCreating}
            certProgress={cloneCertProgress}
            certElapsedTime={cloneCertElapsedTime}
            certError={cloneCertError}
            certSuccess={cloneCertSuccess}
          />
        )
      }
    </div >
  )
}
